import { supabase } from '../utils/supabase';

const MAX_TEMPLATES_PER_USER = 10;
const MAX_NAME_LENGTH = 100;
const MAX_CONTENT_LENGTH = 1000;

class MessageTemplateService {
  /**
   * Get all active templates for the current user
   * @param {string} userId - The user ID
   * @returns {Promise<{success: boolean, data: Array, count: number, error?: string}>}
   */
  async getUserTemplates(userId) {
    if (!userId) {
      return this._createErrorResponse('User ID is required', [], 0);
    }

    try {
      const { data, error } = await supabase
        .from('message_templates')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('usage_count', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[MessageTemplateService] getUserTemplates error:', error);
        throw error;
      }

      return this._createSuccessResponse(data || [], data?.length || 0);
    } catch (error) {
      console.error('[MessageTemplateService] getUserTemplates catch:', error);
      return this._createErrorResponse(error.message || 'Failed to load templates', [], 0);
    }
  }

  /**
   * Get user template count
   * @param {string} userId - The user ID
   * @returns {Promise<{success: boolean, count: number, error?: string}>}
   */
  async getUserTemplateCount(userId) {
    if (!userId) {
      return {
        success: false,
        error: 'User ID is required',
        count: 0
      };
    }

    try {
      // Use RPC function for atomic count
      const { data, error } = await supabase
        .rpc('get_user_template_count', { user_id_param: userId });

      if (error) throw error;

      return {
        success: true,
        count: data || 0
      };
    } catch (error) {
      console.error('[MessageTemplateService] getUserTemplateCount:', error);
      
      // Fallback to direct count
      try {
        const { count, error: countError } = await supabase
          .from('message_templates')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('is_active', true);

        if (countError) throw countError;

        return {
          success: true,
          count: count || 0
        };
      } catch (fallbackError) {
        return {
          success: false,
          error: fallbackError.message || 'Failed to get template count',
          count: 0
        };
      }
    }
  }

  /**
   * Create a new template
   * @param {string} userId - The user ID
   * @param {Object} templateData - Template data {name, content, category}
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  async createTemplate(userId, templateData) {
    if (!userId) {
      return this._createErrorResponse('User ID is required');
    }

    try {
      // Validate template data
      const validation = this._validateTemplateData(templateData);
      if (!validation.isValid) {
        return this._createErrorResponse(validation.error);
      }

      // Check if user has reached the limit
      const countResult = await this.getUserTemplateCount(userId);
      if (!countResult.success) {
        return this._createErrorResponse('Failed to check template limit');
      }

      if (countResult.count >= MAX_TEMPLATES_PER_USER) {
        return this._createErrorResponse(`You can only have up to ${MAX_TEMPLATES_PER_USER} templates. Please delete an existing template first.`);
      }

      // Check for duplicate name
      const { data: existing, error: checkError } = await supabase
        .from('message_templates')
        .select('id')
        .eq('user_id', userId)
        .eq('name', templateData.name.trim())
        .eq('is_active', true)
        .maybeSingle();

      if (checkError) {
        console.error('[MessageTemplateService] Duplicate check error:', checkError);
        throw checkError;
      }

      if (existing) {
        return this._createErrorResponse('A template with this name already exists');
      }

      // Create the template
      const { data, error } = await supabase
        .from('message_templates')
        .insert({
          user_id: userId,
          name: templateData.name.trim(),
          content: templateData.content.trim(),
          category: templateData.category || 'general'
        })
        .select()
        .single();

      if (error) throw error;

      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('[MessageTemplateService] createTemplate:', error);
      
      return {
        success: false,
        error: error.message || 'Failed to create template'
      };
    }
  }

  /**
   * Update an existing template
   * @param {string} templateId - The template ID
   * @param {string} userId - The user ID
   * @param {Object} updates - Update data {name?, content?, category?}
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  async updateTemplate(templateId, userId, updates) {
    if (!templateId || !userId) {
      return {
        success: false,
        error: 'Template ID and User ID are required'
      };
    }

    try {
      // Validate updates
      const validation = this._validateTemplateData(updates, true);
      if (!validation.isValid) {
        return {
          success: false,
          error: validation.error
        };
      }

      // Check if template exists and belongs to user
      const { data: existing, error: checkError } = await supabase
        .from('message_templates')
        .select('id, name')
        .eq('id', templateId)
        .eq('user_id', userId)
        .eq('is_active', true)
        .maybeSingle();

      if (checkError) {
        throw checkError;
      }

      if (!existing) {
        return {
          success: false,
          error: 'Template not found'
        };
      }

      // Check for duplicate name if name is being updated
      if (updates.name && updates.name.trim() !== existing.name) {
        const { data: duplicate, error: duplicateError } = await supabase
          .from('message_templates')
          .select('id')
          .eq('user_id', userId)
          .eq('name', updates.name.trim())
          .eq('is_active', true)
          .neq('id', templateId)
          .maybeSingle();

        if (duplicateError) {
          throw duplicateError;
        }

        if (duplicate) {
          return {
            success: false,
            error: 'A template with this name already exists'
          };
        }
      }

      // Prepare update data
      const updateData = {};
      if (updates.name) updateData.name = updates.name.trim();
      if (updates.content) updateData.content = updates.content.trim();
      if (updates.category) updateData.category = updates.category;

      // Update the template
      const { data, error } = await supabase
        .from('message_templates')
        .update(updateData)
        .eq('id', templateId)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;

      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('[MessageTemplateService] updateTemplate:', error);
      
      return {
        success: false,
        error: error.message || 'Failed to update template'
      };
    }
  }

  /**
   * Delete a template (soft delete)
   * @param {string} templateId - The template ID
   * @param {string} userId - The user ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async deleteTemplate(templateId, userId) {
    if (!templateId || !userId) {
      return {
        success: false,
        error: 'Template ID and User ID are required'
      };
    }

    try {
      // Use RPC function for atomic soft delete
      const { data, error } = await supabase
        .rpc('soft_delete_template', {
          template_id: templateId,
          user_id_param: userId
        });

      if (error) throw error;

      if (!data) {
        return {
          success: false,
          error: 'Template not found or already deleted'
        };
      }

      return {
        success: true
      };
    } catch (error) {
      console.error('[MessageTemplateService] deleteTemplate:', error);
      
      // Fallback to direct update
      try {
        const { error: updateError } = await supabase
          .from('message_templates')
          .update({ is_active: false })
          .eq('id', templateId)
          .eq('user_id', userId)
          .eq('is_active', true);

        if (updateError) throw updateError;

        return {
          success: true
        };
      } catch (fallbackError) {
        return {
          success: false,
          error: fallbackError.message || 'Failed to delete template'
        };
      }
    }
  }

  /**
   * Increment usage count for a template
   * @param {string} templateId - The template ID
   * @param {string} userId - The user ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async incrementUsageCount(templateId, userId) {
    if (!templateId || !userId) {
      return {
        success: false,
        error: 'Template ID and User ID are required'
      };
    }

    try {
      // Use RPC function for atomic increment
      const { error } = await supabase.rpc('increment_template_usage', {
        template_id: templateId,
        user_id_param: userId
      });

      if (error) throw error;

      return {
        success: true
      };
    } catch (error) {
      console.error('[MessageTemplateService] incrementUsageCount:', error);
      
      // Fallback to manual increment
      try {
        const { data: template, error: getError } = await supabase
          .from('message_templates')
          .select('usage_count')
          .eq('id', templateId)
          .eq('user_id', userId)
          .eq('is_active', true)
          .maybeSingle();

        if (getError) throw getError;

        if (!template) {
          return { success: false, error: 'Template not found' };
        }

        const { error: updateError } = await supabase
          .from('message_templates')
          .update({ usage_count: (template.usage_count || 0) + 1 })
          .eq('id', templateId)
          .eq('user_id', userId);

        if (updateError) throw updateError;

        return {
          success: true
        };
      } catch (fallbackError) {
        return {
          success: false,
          error: fallbackError.message || 'Failed to increment usage count'
        };
      }
    }
  }

  /**
   * Get templates by category
   * @param {string} userId - The user ID
   * @param {string} category - The category to filter by
   * @returns {Promise<{success: boolean, data: Array, error?: string}>}
   */
  async getTemplatesByCategory(userId, category) {
    if (!userId) {
      return {
        success: false,
        error: 'User ID is required',
        data: []
      };
    }

    try {
      const query = supabase
        .from('message_templates')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true);

      if (category && category !== 'all') {
        query.eq('category', category);
      }

      const { data, error } = await query
        .order('usage_count', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      return {
        success: true,
        data: data || []
      };
    } catch (error) {
      console.error('[MessageTemplateService] getTemplatesByCategory:', error);
      
      return {
        success: false,
        error: error.message || 'Failed to load templates by category',
        data: []
      };
    }
  }

  /**
   * Validate template data
   * @param {Object} templateData - The template data to validate
   * @param {boolean} isUpdate - Whether this is an update operation
   * @returns {Object} Validation result
   * @private
   */
  _validateTemplateData(templateData, isUpdate = false) {
    if (!templateData || typeof templateData !== 'object') {
      return {
        isValid: false,
        error: 'Invalid template data'
      };
    }

    // For updates, fields are optional
    if (!isUpdate) {
      if (!templateData.name || typeof templateData.name !== 'string') {
        return {
          isValid: false,
          error: 'Template name is required'
        };
      }

      if (!templateData.content || typeof templateData.content !== 'string') {
        return {
          isValid: false,
          error: 'Template content is required'
        };
      }
    }

    // Validate name if provided
    if (templateData.name !== undefined) {
      if (typeof templateData.name !== 'string' || !templateData.name.trim()) {
        return {
          isValid: false,
          error: 'Template name must be a non-empty string'
        };
      }

      if (templateData.name.trim().length > MAX_NAME_LENGTH) {
        return {
          isValid: false,
          error: `Template name must be ${MAX_NAME_LENGTH} characters or less`
        };
      }
    }

    // Validate content if provided
    if (templateData.content !== undefined) {
      if (typeof templateData.content !== 'string' || !templateData.content.trim()) {
        return {
          isValid: false,
          error: 'Template content must be a non-empty string'
        };
      }

      if (templateData.content.trim().length > MAX_CONTENT_LENGTH) {
        return {
          isValid: false,
          error: `Template content must be ${MAX_CONTENT_LENGTH} characters or less`
        };
      }
    }

    // Validate category if provided
    if (templateData.category !== undefined) {
      const validCategories = ['general', 'introduction', 'follow-up', 'interview', 'project'];
      if (!validCategories.includes(templateData.category)) {
        return {
          isValid: false,
          error: 'Invalid category'
        };
      }
    }

    return {
      isValid: true
    };
  }

  /**
   * Get available categories
   * @returns {Array} Array of category objects
   */
  getCategories() {
    return [
      { value: 'general', label: 'General' },
      { value: 'introduction', label: 'Introduction' },
      { value: 'follow-up', label: 'Follow-up' },
      { value: 'interview', label: 'Interview' },
      { value: 'project', label: 'Project' }
    ];
  }

  /**
   * Get template statistics for a user
   * @param {string} userId - The user ID
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  async getTemplateStats(userId) {
    if (!userId) {
      return {
        success: false,
        error: 'User ID is required'
      };
    }

    try {
      const { data, error } = await supabase
        .from('message_templates')
        .select('category, usage_count')
        .eq('user_id', userId)
        .eq('is_active', true);

      if (error) throw error;

      const stats = {
        total: data.length,
        totalUsage: data.reduce((sum, template) => sum + (template.usage_count || 0), 0),
        byCategory: data.reduce((acc, template) => {
          const category = template.category || 'general';
          if (!acc[category]) {
            acc[category] = { count: 0, usage: 0 };
          }
          acc[category].count++;
          acc[category].usage += template.usage_count || 0;
          return acc;
        }, {}),
        mostUsed: data.sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0))[0] || null
      };

      return {
        success: true,
        data: stats
      };
    } catch (error) {
      console.error('[MessageTemplateService] getTemplateStats:', error);
      
      return this._createErrorResponse(error.message || 'Failed to get template statistics');
    }
  }

  // Helper methods for consistent response formatting
  _createSuccessResponse(data, count = null) {
    const response = { success: true, data };
    if (count !== null) response.count = count;
    return response;
  }

  _createErrorResponse(error, data = null, count = null) {
    const response = { success: false, error };
    if (data !== null) response.data = data;
    if (count !== null) response.count = count;
    return response;
  }
}

export default new MessageTemplateService();